import { onBeforeMount } from "vue";
import { useEventListener } from "@vueuse/core";
import useSearchStoreHistory, { searchStoreUrl } from "./use-search-store-history";

/**
 * Wires a search list store (see create-search-list-store) to the page it renders on:
 * the server-rendered sort config, the URL, the back/forward buttons, and the first
 * page of rows.
 *
 * Call it once from `setup`. Everything happens in `onBeforeMount`, so the store is
 * populated before the first render and the list never flashes empty when the server
 * already sent rows.
 *
 * A list that ships `initialData` renders it as-is and skips the initial request; the
 * URL is still read so a shared link arrives with its filters selected. Without it the
 * store loads from whatever the URL says.
 *
 * @param {Object} store - a store built by createSearchListStore
 * @param {Object} options
 * @param {number} options.userId - the user whose records the list is scoped to
 * @param {Object} options.sortConfig - server-rendered `{ options, default_sort_by,
 *   default_sort_direction }`; supplies the default sort
 * @param {Object} [options.initialData=null] - a first page in the response shape
 *   `{ [itemsKey], total_entries, per_page, current_page }`, to render without a round trip
 * @param {string} [options.prefix=""] - namespaces this list's query params, for pages
 *   carrying more than one list
 * @param {string[]} [options.ignoreKeys=["user_id"]] - params the store knows about but
 *   that should never be written to the URL
 * @param {Object} [options.knownIds={}] - available ids by filter key, e.g. `{ supplierIds: [1, 2] }`.
 *   With no list params in the URL, remove saved ids missing from these options before the first load.
 *
 * @example
 * const store = usePurchaseOrderSearchStore();
 *
 * useSearchList(store, {
 *   userId: props.userId,
 *   sortConfig: props.sortConfig,
 *   initialData: props.initialData,
 * });
 */
export function useSearchList(
  store,
  { userId, sortConfig, initialData = null, prefix = "", ignoreKeys = ["user_id"], knownIds = {} }
) {
  onBeforeMount(() => {
    /* Set the account before initializing the sort, since an account change resets the store. */
    store.setUserId(userId);
    store.initSortConfig(sortConfig);

    /* Snapshot the URL before anything can mutate the store under the history
     * subscription below. Once that is live, a mutation rewrites the URL from state
     * restored out of localStorage, losing the params the link was opened with. */
    const originalParams = new URL(location.href).searchParams;
    const params = new URLSearchParams();
    Object.values(store.queryParamMap).forEach((key) => {
      originalParams.getAll(prefix + key).forEach((value) => params.append(key, value));
    });

    const hasNoUrlFilters = params.toString() === "";
    if (hasNoUrlFilters) store.dropUnknownIds(knownIds);

    const loadFromUrl = useSearchStoreHistory(store, prefix, ignoreKeys);
    useEventListener(window, "popstate", () => loadFromUrl());

    const hasSavedChoices = Object.keys(store.configuredFilterKeys).some((key) => key !== "userId");
    if (hasNoUrlFilters && hasSavedChoices) {
      /* Replace this history entry so Back doesn't reopen the URL without list params and clear the saved filters. */
      history.replaceState({}, "", searchStoreUrl(store, prefix, ignoreKeys));
      store.loadPage();
      return;
    }

    if (!initialData) {
      loadFromUrl();
      return;
    }

    store.applyFromSearchParams(params);
    store.hydrate(initialData);
  });
}
